import { useState, useRef, useCallback, useEffect } from 'react';
import { Message } from '@/types';

export interface ChatState {
  // Chat window state
  isChatOpen: boolean;
  isChatMinimized: boolean;
  unreadMessagesCount: number;

  // Messages
  messages: Message[];
  newMessage: string;
  isLoading: boolean;

  // UI state
  error: string | null;
  successMessage: string;
  selectedQuestion: string | null;

  // Modal states
  isLogoutModalOpen: boolean;

  // Refs
  messagesEndRef: React.RefObject<HTMLDivElement | null>;
  messageInputRef: React.RefObject<HTMLInputElement | null>;
  chatWindowRef: React.RefObject<HTMLDivElement | null>;

  // Tracking sets
  countedUnreadMessages: Set<string>;
}

export interface ChatActions {
  // Chat window actions
  openChat: () => void;
  closeChat: () => void;
  minimizeChat: () => void;
  restoreChat: () => void;

  // Message actions
  setMessages: React.Dispatch<React.SetStateAction<Message[]>>;
  setNewMessage: React.Dispatch<React.SetStateAction<string>>;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;

  // Error handling
  setError: (error: string | null) => void;
  setSuccessMessage: React.Dispatch<React.SetStateAction<string>>;

  // Question handling
  handleQuestionClick: (question: string) => void;
  clearSelectedQuestion: () => void;

  // Modal actions
  openLogoutModal: () => void;
  closeLogoutModal: () => void;

  // Unread messages
  incrementUnreadCount: () => void;
  resetUnreadCount: () => void;
}

export function useChatState(): ChatState & ChatActions {
  // Chat window state
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [isChatMinimized, setIsChatMinimized] = useState(false);
  const [unreadMessagesCount, setUnreadMessagesCount] = useState(0);

  // Messages
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // UI state
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [selectedQuestion, setSelectedQuestion] = useState<string | null>(null);

  // Modal states
  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);

  // Refs
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const messageInputRef = useRef<HTMLInputElement | null>(null);
  const chatWindowRef = useRef<HTMLDivElement | null>(null);

  // Tracking sets
  const countedUnreadMessagesRef = useRef<Set<string>>(new Set());

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  // Auto-clear error after 5 seconds
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  // Auto-clear success message after 3 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  // Chat window actions
  const openChat = useCallback(() => {
    setIsChatOpen(true);
    setIsChatMinimized(false);
    resetUnreadCount();
  }, []);

  const closeChat = useCallback(() => {
    setIsChatOpen(false);
    setIsChatMinimized(false);
  }, []);

  const minimizeChat = useCallback(() => {
    setIsChatMinimized(true);
  }, []);

  const restoreChat = useCallback(() => {
    setIsChatMinimized(false);
    resetUnreadCount();
  }, []);

  // Question handling
  const handleQuestionClick = useCallback(
    (question: string) => {
      setSelectedQuestion(question);
      setNewMessage(question);
      openChat();
      // Focus input after a short delay to ensure chat is open
      setTimeout(() => {
        messageInputRef.current?.focus();
      }, 100);
    },
    [openChat]
  );

  const clearSelectedQuestion = useCallback(() => {
    setSelectedQuestion(null);
    setNewMessage('');
  }, []);

  // Modal actions
  const openLogoutModal = useCallback(() => {
    setIsLogoutModalOpen(true);
  }, []);

  const closeLogoutModal = useCallback(() => {
    setIsLogoutModalOpen(false);
  }, []);

  // Unread messages
  const incrementUnreadCount = useCallback(() => {
    setUnreadMessagesCount((prev) => prev + 1);
  }, []);

  const resetUnreadCount = useCallback(() => {
    setUnreadMessagesCount(0);
    countedUnreadMessagesRef.current.clear();
  }, []);

  return {
    // State
    isChatOpen,
    isChatMinimized,
    unreadMessagesCount,
    messages,
    newMessage,
    isLoading,
    error,
    successMessage,
    selectedQuestion,
    isLogoutModalOpen,
    messagesEndRef,
    messageInputRef,
    chatWindowRef,
    countedUnreadMessages: countedUnreadMessagesRef.current,

    // Actions
    openChat,
    closeChat,
    minimizeChat,
    restoreChat,
    setMessages,
    setNewMessage,
    setIsLoading,
    setError,
    setSuccessMessage,
    handleQuestionClick,
    clearSelectedQuestion,
    openLogoutModal,
    closeLogoutModal,
    incrementUnreadCount,
    resetUnreadCount,
  };
}
